import React, { useState, useEffect, useCallback } from 'react';
import { UserProfile, PublicProfile } from '../types';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useTranslation } from '../lib/translations';
import { 
  Sparkles, 
  Loader2, 
  Check, 
  Globe, 
  Eye, 
  Zap, 
  Save, 
  ArrowRight,
  Plus,
  Trash2,
  Image as ImageIcon,
  Video as VideoIcon,
  DollarSign,
  Tag,
  AlignLeft,
  Layout,
  ExternalLink,
  MapPin,
  CheckCircle2,
  X,
  AlertTriangle,
  Navigation
} from 'lucide-react';
import Avatar from '../components/Avatar';
import Tooltip from '../components/Tooltip';
import { useNavigate } from 'react-router-dom';

interface EditPublicProfileProps {
  profile: UserProfile | null;
}

const INSTRUMENTS = [
  'Guitar', 'Piano', 'Voice', 'Drums', 'Violin', 'Bass', 
  'Double Bass', 'Saxophone', 'Trumpet', 'Flute', 'Cello', 
  'Music Theory', 'Composition', 'Mixing', 'Producer'
];

const EditPublicProfile: React.FC<EditPublicProfileProps> = ({ profile }) => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [isPublished, setIsPublished] = useState(false);
  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [tagline, setTagline] = useState('');
  const [bio, setBio] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [remote, setRemote] = useState(true);
  const [coords, setCoords] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [locLoading, setLocLoading] = useState(false);
  const [locSuccess, setLocSuccess] = useState(false);

  const [hourlyRate, setHourlyRate] = useState(40);
  const [selectedInstruments, setSelectedInstruments] = useState<string[]>([]);
  const [youtubeVideoId, setYoutubeVideoId] = useState('');
  const [lessonPacks, setLessonPacks] = useState<{ label: string; price: number; savings?: string }[]>([]);

  useEffect(() => {
    const fetchPublicData = async () => {
      if (!profile) return;
      setLoading(true);
      try {
        const d = await getDoc(doc(db, 'public_profiles', profile.uid));
        if (d.exists()) {
          const data = d.data() as PublicProfile;
          setIsPublished(data.isPublished);
          setDisplayName(data.displayName);
          setTagline(data.tagline);
          setBio(data.bio);
          setCity(data.location.city);
          setCountry(data.location.country);
          setRemote(data.location.remote);
          setCoords(data.location.coords);
          setHourlyRate(data.pricing.hourlyRate);
          setSelectedInstruments(data.instruments);
          setYoutubeVideoId(data.youtubeVideoId || '');
          setLessonPacks(data.pricing.lessonPacks);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchPublicData();
  }, [profile]);

  const toggleInstrument = (inst: string) => {
    setSelectedInstruments(prev => 
      prev.includes(inst) ? prev.filter(i => i !== inst) : [...prev, inst]
    );
  };

  const handleAddPack = () => {
    setLessonPacks([...lessonPacks, { label: language === 'fr' ? 'Nouveau Forfait' : 'New Pack', price: 0, savings: '' }]);
  };

  const handleUpdatePack = (idx: number, field: string, val: any) => {
    const newPacks = [...lessonPacks];
    (newPacks[idx] as any)[field] = val;
    setLessonPacks(newPacks);
  };

  const handleRemovePack = (idx: number) => {
    setLessonPacks(lessonPacks.filter((_, i) => i !== idx));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocLoading(false);
        setLocSuccess(true);
        setTimeout(() => setLocSuccess(false), 3000);
      },
      (err) => {
        console.error(err);
        setLocLoading(false);
      }
    );
  };

  const validate = useCallback(() => {
    if (!displayName.trim()) return language === 'fr' ? "Le nom d'artiste est requis." : "Stage Name is required.";
    if (!tagline.trim()) return language === 'fr' ? "La phrase d'accroche est requise." : "Tagline is required.";
    if (!bio.trim() || bio.trim().length < 50) return language === 'fr' ? "La biographie doit faire au moins 50 caractères." : "Biography must be at least 50 characters.";
    if (selectedInstruments.length === 0) return language === 'fr' ? "Sélectionnez au moins une catégorie." : "Please select at least one teaching category.";
    if (!city.trim() || !country.trim()) return language === 'fr' ? "La ville et le pays sont requis." : "Location (City/Country) is required.";
    return null;
  }, [displayName, tagline, bio, selectedInstruments, city, country, language]);

  const handleSave = async () => {
    if (!profile) return false;
    
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    
    setSaving(true);
    setError(null);
    try {
      const publicData: any = {
        uid: profile.uid,
        displayName,
        tagline,
        bio,
        instruments: selectedInstruments,
        location: { city, country, remote, coords: coords || null },
        avatarUrl: profile.photoURL,
        gallery: [], 
        youtubeVideoId: youtubeVideoId.trim() || null,
        pricing: {
          hourlyRate,
          currency: profile.currency || 'EUR',
          lessonPacks
        },
        isPublished,
        updatedAt: Timestamp.now()
      };
      await setDoc(doc(db, 'public_profiles', profile.uid), publicData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setSaving(false);
      return true;
    } catch (e) {
      console.error(e);
      setError(language === 'fr' ? 'Erreur lors de la sauvegarde.' : 'Failed to save profile. Please try again.');
      setSaving(false);
      return false;
    }
  };

  const handlePreview = async () => {
    const saved = await handleSave();
    if (saved) {
      window.open(`/#/teacher/${profile?.uid}`, '_blank');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSave();
  };

  if (loading) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
      <Loader2 className="animate-spin text-purple-600" size={48} />
      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{language === 'fr' ? 'Harmonisation de la scène...' : 'Syncing stage presence...'}</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-32">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-widest shadow-sm mb-4">
            <Sparkles size={12} className="animate-pulse" /> {t.nav.publicProfile}
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{t.publicProfile.title}</h1>
          <p className="text-slate-500 font-medium">{t.publicProfile.desc}</p>
        </div>
        
        <div className="flex items-center gap-3">
           <button 
             type="button"
             onClick={handlePreview}
             className="px-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:border-purple-500/30 transition-all shadow-sm"
           >
             <Eye size={16} /> {t.common.preview}
           </button>
           <button 
             onClick={handleSave}
             disabled={saving}
             className={`px-10 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 transition-all shadow-xl active:scale-95 ${success ? 'bg-teal-600 text-white' : 'bg-purple-600 text-white shadow-purple-500/20 hover:bg-purple-700'}`}
           >
             {saving ? <Loader2 size={16} className="animate-spin" /> : (success ? <Check size={16} /> : <Save size={16} />)}
             {success ? t.common.done : t.publicProfile.publishChanges}
           </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-xl flex items-center gap-3 animate-in slide-in-from-top-4">
          <AlertTriangle className="text-red-600 shrink-0" size={20} />
          <p className="text-sm font-bold text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-10">
        <div className="bg-slate-900 rounded-2xl p-8 text-white flex flex-col sm:flex-row items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-5">
             <Zap size={100} />
          </div>
          <div className="relative z-10 flex items-center gap-6">
             <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border-2 transition-all duration-500 ${isPublished ? 'bg-teal-500 border-teal-400 shadow-[0_0_20px_rgba(20,184,166,0.4)]' : 'bg-slate-800 border-slate-700'}`}>
                <Globe size={28} className={isPublished ? 'animate-pulse' : 'opacity-40'} />
             </div>
             <div>
                <h3 className="text-xl font-black tracking-tight mb-1">{t.publicProfile.visibility}</h3>
                <p className="text-slate-400 text-sm font-medium">{isPublished ? t.publicProfile.publishDesc : t.publicProfile.unpublishDesc}</p>
             </div>
          </div>
          <div className="relative z-10">
            <button 
              type="button"
              onClick={() => setIsPublished(!isPublished)}
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${isPublished ? 'bg-teal-500' : 'bg-slate-700'}`}
            >
              <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${isPublished ? 'translate-x-9' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-10">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 shadow-sm space-y-8">
              <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">
                <Tag size={16} className="text-purple-600" /> {t.publicProfile.basicInfo}
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.stageName} <span className="text-red-500">*</span></label>
                <input required type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-3 px-4 text-sm font-black focus:ring-4 focus:ring-purple-500/10 outline-none transition-all" />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.tagline} <span className="text-red-500">*</span></label>
                <input required type="text" placeholder={t.publicProfile.taglinePlaceholder} value={tagline} onChange={(e) => setTagline(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-3 px-4 text-sm font-bold focus:ring-4 focus:ring-purple-500/10 outline-none transition-all" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.city} <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input required type="text" value={city} onChange={(e) => setCity(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-3 pl-10 pr-4 text-sm font-bold" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.country} <span className="text-red-500">*</span></label>
                  <input required type="text" value={country} onChange={(e) => setCountry(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-3 px-4 text-sm font-bold" />
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                   <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.preciseLocation} <span className="text-slate-400 font-medium italic">{t.publicProfile.preciseLocationDesc}</span></label>
                </div>
                <button 
                  type="button"
                  onClick={handleUseCurrentLocation}
                  disabled={locLoading}
                  className={`flex items-center justify-center gap-2 p-3 w-full rounded-xl border transition-all ${locSuccess ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 text-teal-700' : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-white/5 text-slate-600'}`}
                >
                  {locLoading ? <Loader2 size={16} className="animate-spin" /> : locSuccess ? <Check size={16} /> : <Navigation size={16} />}
                  <span className="text-xs font-black uppercase tracking-widest">{locSuccess ? t.publicProfile.locationFound : t.publicProfile.useCurrentLocation}</span>
                </button>
                {coords && (
                  <p className="text-[9px] font-black text-slate-400 uppercase text-center tracking-tighter">
                    LAT: {coords.lat.toFixed(4)} • LNG: {coords.lng.toFixed(4)}
                  </p>
                )}
              </div>

              <div className="space-y-2 pt-4">
                <button 
                  type="button"
                  onClick={() => setRemote(!remote)}
                  className={`flex items-center gap-3 p-4 w-full rounded-2xl border transition-all ${remote ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-500/20 text-teal-700 dark:text-teal-400 shadow-inner' : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-white/5 text-slate-500'}`}
                >
                  <VideoIcon size={18} />
                  <span className="text-xs font-black uppercase tracking-widest">{t.common.remote}</span>
                  {remote && <CheckCircle2 size={18} className="ml-auto" />}
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.bio} <span className="text-red-500">*</span></label>
                <textarea 
                  required 
                  rows={8} 
                  placeholder={t.publicProfile.bioPlaceholder} 
                  value={bio} 
                  onChange={(e) => setBio(e.target.value)} 
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl py-4 px-4 text-sm font-medium focus:ring-4 focus:ring-purple-500/10 outline-none transition-all resize-none custom-scrollbar" 
                />
                <p className={`text-[9px] font-bold uppercase text-right ${bio.length < 50 ? 'text-amber-500' : 'text-slate-400'}`}>
                  {bio.length} / 50 {language === 'fr' ? 'caractères min' : 'characters min'}
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 shadow-sm space-y-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  <DollarSign size={16} className="text-teal-600" /> {t.publicProfile.pricing}
                </div>
                <div className="px-3 py-1 bg-teal-50 dark:bg-teal-900/20 text-teal-600 rounded-lg text-[10px] font-black">
                   {language === 'fr' ? 'DEVISE STUDIO :' : 'STUDIO CURRENCY:'} {profile?.currency || 'EUR'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.hourlyRate} <span className="text-red-500">*</span></label>
                <div className="relative">
                  <DollarSign size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(parseInt(e.target.value) || 0)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-3 pl-12 pr-4 text-xl font-black outline-none transition-all" />
                </div>
              </div>

              <div className="space-y-4">
                 <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.lessonPackages} <span className="text-slate-400 font-medium italic">({t.publicProfile.optional})</span></label>
                    <button type="button" onClick={handleAddPack} className="text-[9px] font-black text-purple-600 uppercase tracking-widest flex items-center gap-1 hover:underline">
                      <Plus size={12} /> {t.publicProfile.addPack}
                    </button>
                 </div>
                 
                 <div className="space-y-3">
                   {lessonPacks.map((pack, i) => (
                     <div key={i} className="flex gap-2 animate-in slide-in-from-left-2">
                        <input type="text" placeholder={t.publicProfile.packLabel} value={pack.label} onChange={(e) => handleUpdatePack(i, 'label', e.target.value)} className="flex-1 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-lg py-2 px-3 text-xs font-bold" />
                        <input type="number" placeholder={t.publicProfile.packPrice} value={pack.price} onChange={(e) => handleUpdatePack(i, 'price', parseInt(e.target.value) || 0)} className="w-24 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-lg py-2 px-3 text-xs font-black" />
                        <input type="text" placeholder={t.publicProfile.savingsPercent} value={pack.savings} onChange={(e) => handleUpdatePack(i, 'savings', e.target.value)} className="w-20 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-lg py-2 px-3 text-xs font-bold" />
                        <button type="button" onClick={() => handleRemovePack(i)} className="p-2 text-slate-400 hover:text-red-500"><X size={16} /></button>
                     </div>
                   ))}
                 </div>
              </div>
            </div>
          </div>

          <div className="space-y-10">
             <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 shadow-sm space-y-8">
               <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">
                  <Layout size={16} className="text-indigo-600" /> {t.publicProfile.curriculum}
               </div>

               <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.instruments} <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {INSTRUMENTS.map(inst => (
                      <button 
                        key={inst}
                        type="button"
                        onClick={() => toggleInstrument(inst)}
                        className={`flex items-center gap-2 p-3 rounded-xl border text-[11px] font-bold transition-all ${selectedInstruments.includes(inst) ? 'bg-purple-600 border-transparent text-white shadow-lg' : 'bg-slate-50 dark:bg-slate-950 border-slate-100 dark:border-white/5 text-slate-600 dark:text-slate-400'}`}
                      >
                        {selectedInstruments.includes(inst) ? <Check size={14} /> : <div className="w-3.5" />}
                        {inst}
                      </button>
                    ))}
                  </div>
               </div>
             </div>

             <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 shadow-sm space-y-8">
                <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">
                  <VideoIcon size={16} className="text-amber-600" /> {t.publicProfile.media}
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">{t.publicProfile.videoUrl} (YouTube ID) <span className="text-slate-400 font-medium italic">({t.publicProfile.optional})</span></label>
                  <div className="relative">
                    <VideoIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input type="text" placeholder="e.g. dQw4w9WgXcQ" value={youtubeVideoId} onChange={(e) => setYoutubeVideoId(e.target.value)} className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl py-3 pl-12 pr-4 text-sm font-bold" />
                  </div>
                </div>
             </div>
          </div>
        </div>

        <div className="pt-10 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
           <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest max-w-sm italic">
             {t.publicProfile.profileRequirements}
           </p>
           <button 
             type="submit"
             disabled={saving}
             className={`px-12 py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-3 transition-all shadow-2xl active:scale-95 ${success ? 'bg-teal-600 text-white' : 'bg-purple-600 text-white shadow-purple-500/40 hover:bg-purple-700'}`}
           >
             {saving ? <Loader2 size={18} className="animate-spin" /> : (success ? <Check size={18} /> : <Save size={18} />)}
             {success ? t.common.done : t.publicProfile.publishChanges}
           </button>
        </div>
      </form>
    </div>
  );
};

export default EditPublicProfile;
